import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@scripts/react/components/Button";
import { ImproveLayout } from "./Layout";
import { ImproveSidebarContent } from "./SidebarContent";
import { ImproveVisualAreaContent } from "./VisualAreaContent";
import { useFlowState } from "@/hooks/useFlowState";
import { useClearImproveState, useClearMediaState } from "@/store";
import { DeploySteps, Flows } from "@/types/flows";

export function ImproveModal() {
    const navigate = useNavigate();
    const { computeUrlPath } = useFlowState();
    const clearImproveState = useClearImproveState();
    const clearMediaState = useClearMediaState();

    const handleClose = useCallback(() => {
        // Always navigate to the deploy page for the current workflow
        const deployPath = computeUrlPath({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY });
        navigate(deployPath);
    }, [navigate, computeUrlPath]);

    // Clear improve and media state when unmounting (leaving the page)
    useEffect(() => {
        return () => {
            clearImproveState();
            clearMediaState();
        };
    }, [clearImproveState, clearMediaState]);

    // Handle escape key to close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                handleClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleClose]);

    return (
        <div className="flex h-full w-full flex-col bg-white">
            {/* Header */}
            <div className="mt-4 flex items-center justify-between bg-white px-6">
                <div className="flex items-center gap-2">
                    <h1 className="text-base font-semibold text-gray-900">Improve Model</h1>
                    <span className="text-sm text-gray-500">
                        When you approve an image, it will be used to improve your model. Edit any
                        boxes before approving.
                    </span>
                </div>
                <Button type="secondary" size="xsmall" onClick={handleClose}>
                    <i className="far fa-times"></i>
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                <ImproveLayout
                    Sidebar={<ImproveSidebarContent />}
                    VisualArea={<ImproveVisualAreaContent />}
                />
            </div>
        </div>
    );
}
